import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { ChevronRight, ChevronDown, Building2, Briefcase, Users as UsersIcon, Layers } from "lucide-react";

export default function Structures() {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(new Set());
  const [selected, setSelected] = useState(null);

  const { data: societes } = useQuery({ queryKey: ["soc"], queryFn: async () => (await api.get("/societes", { params: { limit: 200 } })).data.data });
  const { data: directions } = useQuery({ queryKey: ["dir"], queryFn: async () => (await api.get("/directions", { params: { limit: 500 } })).data.data });
  const { data: services } = useQuery({ queryKey: ["svc"], queryFn: async () => (await api.get("/services", { params: { limit: 1000 } })).data.data });
  const { data: equipes } = useQuery({ queryKey: ["eq"], queryFn: async () => (await api.get("/equipes", { params: { limit: 1000 } })).data.data });
  const { data: records } = useQuery({ queryKey: ["recs-all"], queryFn: async () => (await api.get("/user-records", { params: { limit: 5000 } })).data.data });
  const { data: users } = useQuery({ queryKey: ["users-all"], queryFn: async () => (await api.get("/users", { params: { limit: 5000 } })).data.data });
  const usersMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const toggle = (id) => {
    const s = new Set(expanded);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpanded(s);
  };

  const dirsBySoc = useMemo(() => {
    const m = {};
    (directions || []).forEach((d) => { (m[d.societe_id] = m[d.societe_id] || []).push(d); });
    return m;
  }, [directions]);
  const svcsByDir = useMemo(() => {
    const m = {};
    (services || []).forEach((s) => { (m[s.direction_id] = m[s.direction_id] || []).push(s); });
    return m;
  }, [services]);
  const eqsBySvc = useMemo(() => {
    const m = {};
    (equipes || []).forEach((e) => { (m[e.service_id] = m[e.service_id] || []).push(e); });
    return m;
  }, [equipes]);

  const usersOfNode = (kind, id) => {
    if (!records) return [];
    if (kind === "service") return (records || []).filter((r) => r.service_id === id).map((r) => usersMap[r.user_id]).filter(Boolean);
    if (kind === "direction") {
      const svcIds = (svcsByDir[id] || []).map((s) => s.id);
      return (records || []).filter((r) => svcIds.includes(r.service_id)).map((r) => usersMap[r.user_id]).filter(Boolean);
    }
    if (kind === "societe") {
      const dirIds = (dirsBySoc[id] || []).map((d) => d.id);
      const svcIds = (services || []).filter((s) => dirIds.includes(s.direction_id)).map((s) => s.id);
      return (records || []).filter((r) => svcIds.includes(r.service_id)).map((r) => usersMap[r.user_id]).filter(Boolean);
    }
    return [];
  };

  const selectedUsers = selected ? usersOfNode(selected.kind, selected.id) : [];

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="structures-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("structures")}</h1>
        <p className="text-sm text-warm-dark/60 mt-1">Société → Direction → Service → Équipe</p>
      </div>

      <div className="grid grid-cols-[1fr_380px] gap-4">
        <Card className="border-sand shadow-soft p-4 min-h-[500px]">
          {(societes || []).length === 0 ? (
            <div className="p-10 text-center text-warm-dark/50 text-sm">{t("no_data")}</div>
          ) : (
            <div className="space-y-0.5">
              {(societes || []).map((s) => {
                const isOpen = expanded.has(`s-${s.id}`);
                return (
                  <div key={s.id}>
                    <TreeNode icon={Building2} label={s.label} subtitle={s.legal_form} open={isOpen}
                      onToggle={() => toggle(`s-${s.id}`)}
                      onSelect={() => setSelected({ kind: "societe", id: s.id, label: s.label })}
                      depth={0} testId={`tree-soc-${s.id}`} />
                    {isOpen && (dirsBySoc[s.id] || []).map((d) => {
                      const dOpen = expanded.has(`d-${d.id}`);
                      return (
                        <div key={d.id}>
                          <TreeNode icon={Briefcase} label={d.label} subtitle={d.code} open={dOpen}
                            onToggle={() => toggle(`d-${d.id}`)}
                            onSelect={() => setSelected({ kind: "direction", id: d.id, label: d.label })}
                            depth={1} testId={`tree-dir-${d.id}`} />
                          {dOpen && (svcsByDir[d.id] || []).map((sv) => {
                            const svOpen = expanded.has(`sv-${sv.id}`);
                            const eqs = eqsBySvc[sv.id] || [];
                            return (
                              <div key={sv.id}>
                                <TreeNode icon={Layers} label={sv.label} subtitle={sv.code} open={svOpen}
                                  hasChildren={eqs.length > 0}
                                  onToggle={() => toggle(`sv-${sv.id}`)}
                                  onSelect={() => setSelected({ kind: "service", id: sv.id, label: sv.label })}
                                  depth={2} testId={`tree-svc-${sv.id}`} />
                                {svOpen && eqs.map((eq) => (
                                  <TreeNode key={eq.id} icon={UsersIcon} label={eq.label}
                                    onSelect={() => setSelected({ kind: "equipe", id: eq.id, label: eq.label })}
                                    depth={3} leaf testId={`tree-eq-${eq.id}`} />
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="border-sand shadow-soft p-5 h-fit">
          <div className="text-xs font-medium uppercase tracking-wider text-warm-dark/60">Sélection</div>
          {selected ? (
            <>
              <div className="font-heading text-lg font-semibold text-warm-dark mt-1 capitalize">{selected.label}</div>
              <div className="text-xs text-warm-dark/60 mb-3">{selected.kind} · {selectedUsers.length} utilisateur(s)</div>
              <div className="max-h-[500px] overflow-y-auto divide-y divide-sand">
                {selectedUsers.length === 0 ? (
                  <div className="py-4 text-sm text-warm-dark/50">{t("no_data")}</div>
                ) : selectedUsers.slice(0, 100).map((u) => (
                  <div key={u.id} className="py-2 text-sm" data-testid={`sel-user-${u.id}`}>
                    <div className="text-warm-dark font-medium">{u.display_name}</div>
                    <div className="text-xs text-warm-dark/60 mono">{u.mail}</div>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="text-sm text-warm-dark/50 mt-2">Cliquez sur un nœud pour afficher les utilisateurs.</div>}
        </Card>
      </div>
    </div>
  );
}

function TreeNode({ icon: Icon, label, subtitle, open, hasChildren = true, onToggle, onSelect, depth, leaf, testId }) {
  return (
    <div className="flex items-center gap-1 text-sm py-1.5 hover:bg-cream rounded-md cursor-pointer group" style={{ paddingLeft: depth * 20 + 6 }} data-testid={testId}>
      {!leaf && hasChildren ? (
        <button onClick={onToggle} className="w-5 h-5 flex items-center justify-center text-warm-dark/50 hover:text-warm-dark/80">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : <span className="w-5" />}
      <button onClick={onSelect} className="flex items-center gap-2 flex-1 text-left min-w-0">
        <Icon size={14} className="text-warm-dark/60 shrink-0" />
        <span className="text-warm-dark font-medium truncate">{label}</span>
        {subtitle && <span className="text-xs text-warm-dark/50 mono truncate">· {subtitle}</span>}
      </button>
    </div>
  );
}
